import { track } from '@/lib/analytics/track';
import { TrackingEventName } from '@/models/analytics/eventTypes';
import { type DateRange, detectPresetUsed } from '@heidi/design-system/date-range-picker';
import { differenceInDays } from 'date-fns';
import { useAtom } from 'jotai';
import { useCallback, useRef, useState } from 'react';
import { selectedDateRangeFilterAtom } from '../atoms';

const isRangeChanged = (prev: DateRange, current: DateRange): boolean => {
  const normalize = (d?: Date) => (d ? new Date(d).setHours(0, 0, 0, 0) : null);
  return (
    normalize(prev.from) !== normalize(current.from) ||
    normalize(prev.to) !== normalize(current.to)
  );
};

// `emitAnalytics` lets a surface opt out of this hook's `sessions_filter_applied`
// emission. The new session list passes `false` and tracks via the shared
// `useSessionsFilterTracking` chokepoint instead; the old sidebar list uses the
// default and keeps emitting on popover close.
export const useDateFilter = ({
  emitAnalytics = true,
}: { emitAnalytics?: boolean } = {}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedDateRangeFilter, setSelectedDateRangeFilter] = useAtom(
    selectedDateRangeFilterAtom
  );

  // Track the range when picker opens to detect changes on close
  const rangeOnOpenRef = useRef<DateRange>({ from: undefined, to: undefined });
  // Prevent duplicate tracking when setIsOpen(false) is called multiple times
  const hasTrackedDateSelectedRef = useRef(false);

  // Custom setIsOpen that tracks when picker closes
  const handleSetIsOpen = useCallback(
    (open: boolean) => {
      if (!emitAnalytics) {
        setIsOpen(open);
        return;
      }
      if (open) {
        // Store current range when opening and reset tracking flag
        rangeOnOpenRef.current = { ...selectedDateRangeFilter };
        hasTrackedDateSelectedRef.current = false;
      } else if (!hasTrackedDateSelectedRef.current) {
        // When closing, check if range changed and has valid values
        // Only track once per open/close cycle
        const currentRange = selectedDateRangeFilter;
        if (
          currentRange.from &&
          currentRange.to &&
          isRangeChanged(rangeOnOpenRef.current, currentRange)
        ) {
          const presetUsed = detectPresetUsed(currentRange);
          const dateRangeDays = differenceInDays(currentRange.to, currentRange.from) + 1;

          track({
            options: { amplitude: false },
            event_name: TrackingEventName.SessionsFilterApplied,
            props: {
              filter_type: 'date',
              preset_used: presetUsed,
              date_range_days: dateRangeDays,
            },
          });
          hasTrackedDateSelectedRef.current = true;
        }
      }
      setIsOpen(open);
    },
    [selectedDateRangeFilter, emitAnalytics]
  );

  return {
    isOpen,
    setIsOpen: handleSetIsOpen,
    selectedDateRangeFilter,
    setSelectedDateRangeFilter,
  };
};
